import { initializeApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import { getFirestore, collection, addDoc, query, where, orderBy, getDocs, updateDoc, doc } from 'firebase/firestore';
import { markAllNotificationsAsReadFromServer } from '@/api/strapi/notification';

// Cấu hình Firebase của bạn
// Thông thường, các giá trị này nên được lưu trong file .env
const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
  measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID
};

// Biến để theo dõi việc khởi tạo
let firebaseAppInstance = null;
let messagingInstance = null;
let dbInstance = null;

// Hàm khởi tạo Firebase với kiểm tra trùng lặp
function initFirebase() {
  if (!firebaseAppInstance) {
    console.log('Khởi tạo Firebase lần đầu tiên');
    firebaseAppInstance = initializeApp(firebaseConfig);
    messagingInstance = getMessaging(firebaseAppInstance);
    dbInstance = getFirestore(firebaseAppInstance);
  } else {
    console.log('Firebase đã được khởi tạo trước đó');
  }
  
  return {
    firebaseApp: firebaseAppInstance,
    messaging: messagingInstance,
    db: dbInstance
  };
}

// Khởi tạo Firebase
const { firebaseApp, messaging, db } = initFirebase();

// Export các instance
export { firebaseApp, messaging, db };

// Hàm khởi tạo Firebase, sử dụng thay thế cho installFirebase trước đây
// Chỉ trả về các instance đã được khởi tạo, không khởi tạo lại
export function initializeFirebase() {
  console.log('Lấy các instance Firebase đã khởi tạo');
  return {
    firebaseApp,
    messaging,
    db
  };
}

// Hàm yêu cầu quyền thông báo và lấy FCM token
export async function requestNotificationPermission() {
  try {
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      return await getFCMToken();
    }
    
    console.log('Quyền thông báo không được cấp');
    return null;
  } catch (error) {
    console.error('Lỗi khi yêu cầu quyền thông báo:', error);
    return null;
  }
}

export async function getFCMToken() {
  try {
    // Kiểm tra xem vapidKey có tồn tại không
    if (!process.env.VUE_APP_FIREBASE_VAPID_KEY) {
      return null;
    }
    
    // Truyền vapidKey như một tham số bắt buộc
    const currentToken = await getToken(messaging, {
      vapidKey: process.env.VUE_APP_FIREBASE_VAPID_KEY
    });

    console.log('FCM Token:', currentToken);
    
    if (currentToken) {
      return currentToken;
    } else {
      console.log('Không thể lấy token. Yêu cầu quyền thông báo.');
      return null;
    }
  } catch (error) {
    console.error('Lỗi khi lấy FCM Token:', error);
    return null;
  }
}

// Hàm khởi tạo FCM sau khi đăng nhập
// Hàm này trả về true nếu nên hiển thị modal hỏi người dùng, false nếu không cần
export async function initFCMAfterLogin() {
  try {
    console.log('Khởi tạo FCM sau khi đăng nhập...');
    
    // Xóa trạng thái đã hỏi để test
    // localStorage.removeItem('notificationPrompted');
    
    // Kiểm tra xem trình duyệt có hỗ trợ Notification API không
    if (!('Notification' in window)) {
      console.log('Trình duyệt không hỗ trợ thông báo');
      return false;
    }
    
    // Lấy dữ liệu người dùng từ localStorage
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      console.log('Không tìm thấy dữ liệu người dùng');
      return false;
    }
    
    try {
      const userData = JSON.parse(userStr);
      console.log('Dữ liệu người dùng:', userData);
      console.log('Trạng thái thông báo:', userData?.user?.notification_enabled);
      
      // Kiểm tra xem người dùng đã bật thông báo chưa
      const notificationEnabled = userData?.user?.notification_enabled === 'true' || userData?.user?.notification_enabled === true;
      if (notificationEnabled) {
        console.log('Người dùng đã bật thông báo trước đó');
        if (Notification.permission === 'granted') {
          console.log('Quyền thông báo đã được cấp');
          return false;
        }
      }
    } catch (e) {
      console.error('Lỗi khi xử lý dữ liệu người dùng:', e);
    }
    
    // Kiểm tra trạng thái quyền thông báo hiện tại
    const permissionStatus = Notification.permission;
    console.log('Trạng thái quyền thông báo:', permissionStatus);
    
    // Nếu người dùng đã từ chối, không hỏi nữa
    if (permissionStatus === 'denied') {
      console.log('Người dùng đã từ chối thông báo trước đó');
      return false;
    }
    
    // Kiểm tra xem đã hỏi người dùng gần đây chưa
    const hasBeenPrompted = localStorage.getItem('notificationPrompted');
    console.log('Thời gian cuối cùng hỏi:', hasBeenPrompted);
    
    if (hasBeenPrompted) {
      const promptedTime = parseInt(hasBeenPrompted);
      const currentTime = Date.now();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      
      if (currentTime - promptedTime < sevenDays) {
        console.log('Người dùng đã được hỏi trong 7 ngày gần đây');
        // Bỏ qua kiểm tra này để test
        // return false;
      }
    }
    
    // Lưu thời gian hỏi
    localStorage.setItem('notificationPrompted', Date.now().toString());
    
    console.log('Nên hiển thị modal hỏi người dùng');
    // Luôn trả về true để hỏi người dùng trong quá trình phát triển
    return true;
  } catch (error) {
    console.error('Lỗi khi kiểm tra trạng thái thông báo:', error);
    return false;
  }
}

// Hàm xóa FCM token và hủy đăng ký nhận thông báo khi đăng xuất
export async function clearFCMToken() {
  try {
    // Xóa FCM token trong localStorage
    
    // Hủy đăng ký Service Worker
    const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    if (registration) {
      const unsubscribeResult = await registration.pushManager.getSubscription();
      if (unsubscribeResult) {
        await unsubscribeResult.unsubscribe();
        console.log('Hủy đăng ký nhận thông báo thành công');
      }
    }
    
    // Tắt thông báo trên trình duyệt
    if ('Notification' in window) {
      // Đặt trạng thái thông báo về mặc định (chưa được cấp quyền)
      // Lưu ý: Không thể hoàn toàn hủy quyền đã cấp, nhưng có thể ngăn chặn việc gửi thông báo
      console.log('Tắt thông báo trên trình duyệt');
    }
  } catch (error) {
    console.error('Lỗi khi hủy đăng ký nhận thông báo:', error);
  }
}